'use client';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/components/ui/utils';
import { GuestV2Segmented } from '@/components/guest-v2/GuestV2';
import { Loader2, ArrowLeft, CheckCircle2, Fingerprint, Mail, Smartphone, User } from 'lucide-react';
import { customerFetch, customerSignIn, isCustomerOrigin } from '@/lib/customer-bridge';
import { claimGuestDeviceData } from '@/lib/customer/claim-guest-data';
import { detectDeviceCountry, isSupportedCountry, resolveCountryIso2 } from '@/lib/config-system/countries';
import { BranchPaletteContext } from '@/components/customer-surface/BranchThemeScope';

type ModalView =
  | 'choose'
  | 'otp-verify'
  | 'phone-verify'
  | 'passkey-auth'
  | 'passkey-register'
  | 'success'
  | 'name-prompt';
// The concrete views a caller may open the modal on (initialView). Exported so
// surfaces like the Chow settings Security section can pass one through context
// without re-declaring the union.
export type CustomerAuthModalView = ModalView;

interface CustomerAuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context?: string;
  contentStyle?: React.CSSProperties;
  overlayStyle?: React.CSSProperties;
  /** Called once a session is confirmed (post-auth profile flow completes). */
  onAuthenticated?: () => void;
  /**
   * Open directly on this view instead of the sign-in chooser. Used by the
   * dashboard's "Add a passkey" entry -- passkey registration requires an
   * already-verified session, so it is only reachable from signed-in surfaces.
   */
  initialView?: ModalView;
  /** Applies the Chow mobile-first treatment without changing auth behavior. */
  chow?: boolean;
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

// Minimal Apple mark, matching GoogleIcon's size/style contract (className-driven
// sizing, aria-hidden, currentColor so it reads on light and dark buttons).
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.17 3.06-.9 1.04-2.06 1.64-3.16 1.55-.13-1.1.42-2.27 1.13-3.1.83-.94 2.23-1.6 3.2-1.66.01.05.01.1.01.15zM20.5 17.03c-.55 1.27-.82 1.84-1.53 2.97-.99 1.57-2.38 3.53-4.11 3.55-1.54.01-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.79-4.04-3.36-2.76-4.4-3.05-9.57-1.35-12.31 1.21-1.95 3.12-3.09 4.91-3.09 1.83 0 2.98 1 4.49 1 1.47 0 2.36-1 4.48-1 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.83.34 9.36z" />
    </svg>
  );
}

// Client-side mirror of lib/server/customer-profile.ts's normalizeUsername ,
// keeps the suggested handle consistent with what the server will accept.
function suggestUsername(name: string, email?: string): string {
  const base = (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  if (base.length >= 3) return base;
  if (email) {
    const fromEmail = (email.split('@')[0] || '')
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 24);
    if (fromEmail.length >= 3) return fromEmail;
  }
  return '';
}

// Maps the Auth.js sign-in `code` (see CustomerSignInError in
// lib/auth/customer-auth.ts) back to a friendly message. Codes arrive via
// `res.code` on the Credentials `signIn` result; without this mapping a
// failed/expired passkey ceremony used to surface as the opaque
// "Passkey sign-in failed."
const PASSKEY_ERROR_MESSAGES: Record<string, string> = {
  'challenge-expired': 'That passkey attempt expired. Please try again.',
  'passkey-not-recognized': 'No passkey for this device is linked to your account. Try signing in with your email instead.',
  'verification-failed': 'We could not verify your passkey. Please try again.',
  'passkey-error': 'Passkey sign-in failed. Please try again.',
};

function passkeyErrorMessage(code: string): string {
  return PASSKEY_ERROR_MESSAGES[code] || PASSKEY_ERROR_MESSAGES['passkey-error'];
}

// The country a customer last picked, remembered across visits so the picker
// does not reset to the device default every time. Written only on an explicit
// pick; the device-timezone default (detectDeviceCountry) is never persisted so
// it stays a fallback rather than a sticky surprise.
const COUNTRY_STORAGE_KEY = 'menuraq.customer.country';

function formatCooldown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-foreground">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        className="h-10 text-sm"
      />
    </div>
  );
}

export function CustomerAuthModal({
  open,
  onOpenChange,
  contentStyle,
  overlayStyle,
  onAuthenticated,
  initialView,
  chow = false,
}: CustomerAuthModalProps) {
  const branchPaletteStyle = useContext(BranchPaletteContext);
  const authContentStyle = branchPaletteStyle || contentStyle
    ? { ...branchPaletteStyle, ...contentStyle }
    : undefined;
  const openerRef = useRef<HTMLElement | null>(null);
  const [view, setView] = useState<ModalView>('choose');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  // The region a bare national number is read in, and the country recorded on a
  // first-time account. Defaults from the device locale (never geolocation);
  // the phone picker can change it.
  const [country, setCountry] = useState('GH');
  /** Which destination the Chow sign-in asks for. The app segments the choice
   *  rather than stacking two forms, so only one field is ever on screen. */
  const [method, setMethod] = useState<'phone' | 'email'>('phone');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isPhoneOtpLoading, setIsPhoneOtpLoading] = useState(false);
  const [isPhoneVerifyLoading, setIsPhoneVerifyLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [isNameLoading, setIsNameLoading] = useState(false);
  // Marketing opt-in captured at signup (GRAPH-02 R05.4). Only shown for
  // genuinely new accounts; returning accounts manage consent in settings.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [isNewSignup, setIsNewSignup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Seconds left before a code can be re-requested. Mirrors the server-side
  // resend cooldown (lib/server/customer-account-phone-otp.ts) so the button
  // counts down instead of stacking duplicate codes.
  const [resendCooldown, setResendCooldown] = useState(0);
  // If the phone request reaches the server but the browser loses the response
  // (for example, a cross-origin read is blocked after SMS dispatch), preserve
  // the path to enter a code that may already be on its way.
  const [phoneOtpDeliveryUnconfirmed, setPhoneOtpDeliveryUnconfirmed] = useState(false);
  // On-demand email delivery for the phone code: a customer whose text message
  // is stuck in carrier approval can pull the code to an inbox instead.
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [emailFallbackAddress, setEmailFallbackAddress] = useState('');
  const [isPhoneEmailFallbackLoading, setIsPhoneEmailFallbackLoading] = useState(false);
  const searchParams = useSearchParams();
  // On the customer subdomain the default destination is the dashboard; on a
  // main-domain page (the public menu) the customer should land right back on
  // the page they signed in from.
  const onSubdomain = isCustomerOrigin();
  // A passkey only works from an origin covered by its Relying Party ID. With
  // no shared RP ID configured, credentials are bound to `c.menuraq.com` alone
  // and offering them on the public menu would fail in the browser -- hence the
  // subdomain-only gate. Once WEBAUTHN_RP_ID names the registrable domain
  // (`menuraq.com`), one credential covers both hosts and the option can show
  // everywhere. Static member access so Next inlines it into the client bundle.
  const canUsePasskey = onSubdomain || Boolean(process.env.NEXT_PUBLIC_WEBAUTHN_RP_ID);
  // Land the visitor back where they started. On the legacy subdomain this is
  // /c; once Chow is served from chow.menuraq.com (same origin as the customer
  // auth endpoints) it stays on the Chow surface instead of bouncing to a
  // retired /c route.
  const callbackUrl =
    searchParams.get('callbackUrl') ||
    (typeof window !== 'undefined' ? window.location.href : '/');

  // Default the country picker on mount. The device's timezone tells us the
  // market a number will actually be dialled in (far better than UI language,
  // which is US/GB for many Ghana/Nigeria users); a country the customer chose
  // last time wins over that. Users can always change it with the picker.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    } catch {
      stored = null;
    }
    setCountry((current) => {
      if (current !== 'GH') return current;
      if (stored && isSupportedCountry(stored)) return resolveCountryIso2(stored);
      return detectDeviceCountry();
    });
  }, []);

  // Remember an explicit pick so the next visit starts where the customer left
  // off. The device default is not written here (only an onCountryCodeChange
  // pick changes `country` after mount), so it never becomes a sticky value.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, country);
    } catch {
      /* storage full/blocked — the picker still works, it just won't remember */
    }
  }, [country]);

  const resetState = useCallback(() => {
    setView('choose');
    setEmail('');
    setOtp('');
    setPhoneNumber('');
    setFullName('');
    setUsername('');
    setChallengeId('');
    setError(null);
    setSuccessMessage(null);
    setResendCooldown(0);
    setPhoneOtpDeliveryUnconfirmed(false);
    setShowEmailFallback(false);
    setEmailFallbackAddress('');
    setIsPhoneEmailFallbackLoading(false);
    setIsGoogleLoading(false);
    setIsEmailLoading(false);
    setIsOtpLoading(false);
    setIsPhoneOtpLoading(false);
    setIsPhoneVerifyLoading(false);
    setIsPasskeyLoading(false);
    setIsNameLoading(false);
    setMarketingOptIn(false);
    setIsNewSignup(false);
  }, []);

  // Ticks the resend countdown down once a second while one is running.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // A caller can open straight into a flow (e.g. "Add a passkey" from the
  // signed-in dashboard). Reset does not re-apply this on every render -- only
  // when the dialog opens again with the prop set.
  useEffect(() => {
    if (open && initialView) {
      setView(initialView);
    }
  }, [open, initialView]);

  const startResendCooldown = useCallback((seconds: number) => {
    setResendCooldown(Math.max(0, Math.floor(seconds || 0)));
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  }, [onOpenChange, resetState]);

  const checkPostAuthState = useCallback(async () => {
    try {
      const res = await customerFetch('/api/customer-auth/profile');
      const data = await res.json();
      if (!data?.ok) {
        // Sign-in itself already succeeded (the caller only reaches this point
        // after `customerSignIn` returned ok); only the profile READ failed.
        // Returning here left the guest on the OTP screen with a cleared
        // spinner and no message — a silent hang. Take the same success path
        // the network-error branch below takes.
        onAuthenticated?.();
        setView('success');
        setTimeout(() => handleOpenChange(false), 1200);
        return;
      }
      onAuthenticated?.();
      // Doc 52: this device's guest orders and reservation requests belong to
      // the account the moment the guest signs in, so the temporary guest
      // handle can be dropped. Best-effort and idempotent.
      void claimGuestDeviceData();
      const p = data.profile;
      setFullName(p.displayName || '');
      setUsername(p.username || '');
      const createdAt = p.createdAt ? new Date(p.createdAt).getTime() : 0;
      const isNewAccount = createdAt > 0 && Date.now() - createdAt < 2 * 60 * 1000;
      // Every signup , Google, email code, or passkey , lands here once so the
      // customer picks the name we represent them by and the username (handle)
      // we use to recognize them across businesses. Existing accounts only see
      // this again if they never set a display name.
      if (isNewAccount || !p.displayName) {
        setIsNewSignup(isNewAccount);
        setView('name-prompt');
        return;
      }
      setView('success');
      setTimeout(() => handleOpenChange(false), 1200);
    } catch {
      onAuthenticated?.();
      setView('success');
      setTimeout(() => handleOpenChange(false), 1200);
    }
  }, [handleOpenChange, onAuthenticated]);

  const handleGoogleSignIn = useCallback(async () => {
    setIsGoogleLoading(true);
    setError(null);
    try {
      const result = await customerSignIn('google', { callbackUrl, redirect: true });
      if (result?.error || !result?.url) {
        setError('Could not start Google sign-in.');
    setIsGoogleLoading(false);
    setIsAppleLoading(false);
      }
    } catch {
      setError('Could not start Google sign-in.');
      setIsGoogleLoading(false);
    }
  }, [callbackUrl]);

  const handleAppleSignIn = useCallback(async () => {
    setIsAppleLoading(true);
    setError(null);
    try {
      const result = await customerSignIn('apple', { callbackUrl, redirect: true });
      if (result?.error || !result?.url) {
        setError('Could not start Apple sign-in.');
        setIsAppleLoading(false);
      }
    } catch {
      setError('Could not start Apple sign-in.');
      setIsAppleLoading(false);
    }
  }, [callbackUrl]);

  const handleRequestOtp = useCallback(async () => {
    if (!email.trim()) return;
    setIsEmailLoading(true);
    setError(null);
    try {
      const res = await customerFetch('/api/customer-auth/email-otp/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (data.ok) {
        setView('otp-verify');
        setSuccessMessage('Code sent! Check your inbox.');
        startResendCooldown(60);
      } else {
        setError(data.error || 'Failed to send code. Try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsEmailLoading(false);
    }
  }, [email, startResendCooldown]);

  const handleVerifyOtp = useCallback(async () => {
    if (!otp.trim() || otp.trim().length !== 6) return;
    setIsOtpLoading(true);
    setError(null);
    try {
      const result = await customerSignIn('customer-email-otp', {
        email: email.trim(),
        code: otp.trim(),
        redirect: false,
      });
      if (result?.error) {
        setError('Invalid or expired code.');
      } else if (result?.ok) {
        await checkPostAuthState();
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsOtpLoading(false);
    }
  }, [otp, email, checkPostAuthState]);

  const handleRequestPhoneOtp = useCallback(async () => {
    const number = phoneNumber.trim();
    if (!number) return;
    setIsPhoneOtpLoading(true);
    setError(null);
    setOtp('');
    setPhoneOtpDeliveryUnconfirmed(false);
    try {
      const res = await customerFetch('/api/customer-auth/phone-otp/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: number, country }),
      });
      const data = await res.json();
      if (data.ok) {
        setView('phone-verify');
        setShowEmailFallback(false);
        setEmailFallbackAddress(email.trim());
        startResendCooldown(data.retryAfterSeconds ?? 60);
        setSuccessMessage(
          data.emailFallback
            ? `We couldn't text ${number}. Code sent to your email instead.`
            : 'Code sent! Check your phone.',
        );
      } else if (res.status >= 500) {
        // A server error can happen after the SMS provider accepted the text
        // (for example, if a later delivery-log step fails). Keep code entry
        // available because the already-issued code may still be valid.
        setPhoneOtpDeliveryUnconfirmed(true);
        setView('phone-verify');
        startResendCooldown(60);
        setSuccessMessage(null);
      } else if (data.code === 'RESEND_TOO_SOON' && data.retryAfterSeconds) {
        startResendCooldown(data.retryAfterSeconds);
        setError(data.error || 'Wait a moment before requesting another code.');
      } else {
        setError(data.error || 'Failed to send code. Try again.');
      }
    } catch {
      // The route can finish SMS dispatch before the browser fails to read its
      // response (notably on a customer-host CORS/network interruption). The
      // server may already have issued a valid code, so keep the guest moving
      // to code entry and offer a resend after the normal cooldown.
      setPhoneOtpDeliveryUnconfirmed(true);
      setView('phone-verify');
      startResendCooldown(60);
      setSuccessMessage(null);
    } finally {
      setIsPhoneOtpLoading(false);
    }
  }, [phoneNumber, country, startResendCooldown]);

  const handlePhoneOtpViaEmail = useCallback(async () => {
    const address = emailFallbackAddress.trim();
    if (!address) {
      setError('Enter your email address.');
      return;
    }
    setIsPhoneEmailFallbackLoading(true);
    setError(null);
    try {
      const res = await customerFetch('/api/customer-auth/phone-otp/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber.trim(), email: address, delivery: 'email', country }),
      });
      const data = await res.json();
      if (data.ok) {
        startResendCooldown(data.retryAfterSeconds ?? 60);
        setShowEmailFallback(false);
        setSuccessMessage(`Code sent to ${address}. Check your inbox.`);
      } else if (data.code === 'RESEND_TOO_SOON' && data.retryAfterSeconds) {
        startResendCooldown(data.retryAfterSeconds);
        setError(data.error || 'Wait a moment before requesting another code.');
      } else {
        setError(data.error || 'Failed to send code. Try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsPhoneEmailFallbackLoading(false);
    }
  }, [emailFallbackAddress, phoneNumber, country, startResendCooldown]);

  const handleVerifyPhoneOtp = useCallback(async () => {
    if (!otp.trim() || otp.trim().length !== 6) return;
    setIsPhoneVerifyLoading(true);
    setError(null);
    try {
      const result = await customerSignIn('customer-phone-otp', {
        phoneNumber: phoneNumber.trim(),
        code: otp.trim(),
        country: resolveCountryIso2(country),
        redirect: false,
      });
      if (result?.error) {
        setError('Invalid or expired code.');
      } else if (result?.ok) {
        await checkPostAuthState();
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsPhoneVerifyLoading(false);
    }
  }, [otp, phoneNumber, country, checkPostAuthState]);

  const handlePasskeyAuth = useCallback(async (mode: 'register' | 'authenticate') => {
    setIsPasskeyLoading(true);
    setError(null);
    try {
      const optionsEndpoint = mode === 'register'
        ? '/api/customer-auth/passkey/register-options'
        : '/api/customer-auth/passkey/auth-options';

      const optionsInit: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      };
      if (mode === 'register') {
        // Registration is session-bound: the account (and phone, if any) comes
        // from the verified sign-in on the server, not from the request body.
        optionsInit.body = JSON.stringify({
          displayName: fullName.trim() || undefined,
        });
      }

      const optionsRes = await customerFetch(optionsEndpoint, optionsInit);
      const optionsData = await optionsRes.json();
      if (!optionsData.ok || !optionsData.options) {
        setError(optionsData.error || 'Failed to start passkey flow.');
        setIsPasskeyLoading(false);
        return;
      }

      const { startRegistration, startAuthentication } = await import('@simplewebauthn/browser');
      let credential: Credential | undefined;
      if (mode === 'register') {
        credential = await startRegistration({ optionsJSON: optionsData.options });
      } else {
        credential = await startAuthentication({ optionsJSON: optionsData.options });
      }

      setChallengeId(optionsData.challengeId);
      const signInResult = await customerSignIn('customer-passkey', {
        mode,
        challengeId: optionsData.challengeId,
        response: JSON.stringify(credential),
        redirect: false,
      });

      if (signInResult?.error) {
        const code =
          typeof signInResult.code === 'string'
            ? signInResult.code
            : signInResult.error === 'CredentialsSignin'
              ? 'passkey-error'
              : 'passkey-error';
        setError(passkeyErrorMessage(code));
      } else if (signInResult?.ok) {
        await checkPostAuthState();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('NotAllowedError')) {
        setError('Passkey attempt was cancelled.');
      } else {
        setError('Passkey sign-in failed. Please try again.');
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  }, [fullName, checkPostAuthState]);

  const handleSetProfile = useCallback(async () => {
    if (!fullName.trim()) {
      setError('Enter your name.');
      return;
    }
    setIsNameLoading(true);
    setError(null);
    try {
      const body: Record<string, string> = { displayName: fullName.trim() };
      const trimmedUsername = username.trim().toLowerCase();
      if (trimmedUsername) body.username = trimmedUsername;
      const res = await customerFetch('/api/customer-auth/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setError(data?.error || 'Failed to save your profile.');
        return;
      }
      if (isNewSignup && marketingOptIn) {
        void customerFetch('/api/customer-auth/consent-signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }).catch((err) => {
          console.warn('[consent] Marketing consent recording failed (non-blocking):', err);
        });
      }
      setView('success');
      setTimeout(() => handleOpenChange(false), 1200);
    } catch {
      setError('Failed to save your profile. Please try again.');
    } finally {
      setIsNameLoading(false);
    }
  }, [fullName, username, marketingOptIn, isNewSignup, handleOpenChange]);

  const titles: Record<ModalView, string> = {
    choose: 'Sign in or create account',
    'otp-verify': 'Enter code',
    'phone-verify': 'Enter code',
    'passkey-auth': 'Use your passkey',
    'passkey-register': 'Create a passkey',
    success: 'Welcome!',
    'name-prompt': 'Set up your profile',
  };

  /**
   * THE CHOW SIGN-IN, AS THE APP DRAWS IT (`app/auth/phone.tsx`).
   *
   * A guest who taps "Sign in" inside the V2 menu stays on the menu, so this is
   * one decision, not a form: phone or email, one field, "Send code". The V1
   * composition below asks both at once — two labelled fields, an "or" rule and
   * two buttons — which reads as a settings panel and buries the primary action.
   *
   * Everything after this screen (code entry, name) still runs through the
   * same handlers and views; only the entry screen is composed differently,
   * and only when `chow` is set, so V1 surfaces are untouched.
   */
  if (chow && view === 'choose') {
    const contactReady = method === 'phone' ? Boolean(phoneNumber.trim()) : Boolean(email.trim());
    const sending = method === 'phone' ? isPhoneOtpLoading : isEmailLoading;

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          overlayStyle={overlayStyle}
          style={authContentStyle}
          onOpenAutoFocus={() => {
            const document = globalThis.document;
            const activeElement = document.activeElement;
            const HTMLElementConstructor = document.defaultView?.HTMLElement;
            openerRef.current =
              HTMLElementConstructor && activeElement instanceof HTMLElementConstructor && activeElement !== document.body
                ? activeElement
                : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const opener = openerRef.current;
            openerRef.current = null;
            if (opener?.isConnected) opener.focus();
          }}
          className="chow-auth-modal chow-auth-modal--composed chow-root"
        >
          <div className="chow-auth__head">
            <DialogTitle className="chow-auth__title">Sign in or sign up</DialogTitle>
          </div>

          <div className="chow-auth__body">
            <p className="chow-auth__lede">
              {method === 'phone'
                ? "We'll text you a 6-digit code. New here? Your account is created when you verify."
                : "We'll email you a 6-digit code. New here? Your account is created when you verify."}
            </p>

            <GuestV2Segmented
              label="Sign in with"
              options={[
                { value: 'phone', label: 'Phone' },
                { value: 'email', label: 'Email' },
              ] as const}
              value={method}
              onChange={(next) => {
                setMethod(next);
                setError(null);
              }}
            />

            {method === 'phone' ? (
              <label className="chow-auth__field">
                <span className="chow-auth__label">Phone number</span>
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  countryCode={country}
                  onCountryCodeChange={setCountry}
                />
              </label>
            ) : (
              <label className="chow-auth__field">
                <span className="chow-auth__label">Email address</span>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  className="h-12 rounded-[14px] text-[15px]"
                />
              </label>
            )}

            {error ? (
              <p className="chow-auth__error" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <div className="chow-auth__foot">
            <button
              type="button"
              className="chow-auth__cta"
              disabled={sending || !contactReady}
              onClick={() => {
                if (method === 'phone') void handleRequestPhoneOtp();
                else void handleRequestOtp();
              }}
            >
              {sending ? 'Sending…' : 'Send code'}
            </button>
            <p className="chow-auth__terms">
              By continuing you agree to Chow&rsquo;s{' '}
              <a href="/terms" target="_blank" rel="noreferrer">
                Terms &amp; Conditions
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
              {method === 'phone'
                ? ' Standard SMS rates may apply.'
                : ' Check your spam folder if the email doesn’t arrive.'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  /**
   * Code entry, in the same shell as the choice above. The app treats it as a
   * continuation of one screen ("Enter your code"), not a new dialog, so the
   * title and the back arrow swap while the surface stays put.
   */
  if (chow && (view === 'phone-verify' || view === 'otp-verify')) {
    const isPhone = view === 'phone-verify';
    const destination = isPhone ? phoneNumber : email;
    const codeReady = otp.trim().length === 6;
    const verifying = isPhone ? isPhoneVerifyLoading : isOtpLoading;
    const masked = (() => {
      if (!destination) return 'your number';
      if (!isPhone) {
        const [user, domain] = destination.split('@');
        return domain ? `${user.slice(0, 2)}${'*'.repeat(Math.max(1, user.length - 2))}@${domain}` : destination;
      }
      return destination.length > 7
        ? `${destination.slice(0, 7)}${'*'.repeat(Math.max(0, destination.length - 9))}${destination.slice(-2)}`
        : destination;
    })();

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          overlayStyle={overlayStyle}
          style={authContentStyle}
          onOpenAutoFocus={() => {
            const document = globalThis.document;
            const activeElement = document.activeElement;
            const HTMLElementConstructor = document.defaultView?.HTMLElement;
            openerRef.current =
              HTMLElementConstructor && activeElement instanceof HTMLElementConstructor && activeElement !== document.body
                ? activeElement
                : null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const opener = openerRef.current;
            openerRef.current = null;
            if (opener?.isConnected) opener.focus();
          }}
          className="chow-auth-modal chow-auth-modal--composed chow-root"
        >
          <div className="chow-auth__head chow-auth__head--back">
            <button
              type="button"
              className="chow-auth__back"
              aria-label="Back"
              onClick={() => {
                setError(null);
                setView('choose');
              }}
            >
              <ArrowLeft aria-hidden="true" className="size-5" />
            </button>
            <DialogTitle className="chow-auth__title">Enter your code</DialogTitle>
          </div>

          <div className="chow-auth__body">
            <p className="chow-auth__lede" role="status">
              {phoneOtpDeliveryUnconfirmed ? (
                <>We couldn&rsquo;t confirm delivery. If the text arrives, enter its code below. You can resend after the timer.</>
              ) : (
                <>We sent a 6-digit code to <span className="chow-auth__destination">{masked}</span>. Enter it below to verify.</>
              )}
            </p>

            <label className="chow-auth__field">
              <span className="sr-only">6-digit code</span>
              <input
                className="chow-auth__code"
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
              />
            </label>

            {error ? (
              <p className="chow-auth__error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="chow-auth__resend">
              <button
                type="button"
                disabled={resendCooldown > 0 || isPhoneOtpLoading || isEmailLoading}
                onClick={() => {
                  if (isPhone) void handleRequestPhoneOtp();
                  else void handleRequestOtp();
                }}
              >
                {resendCooldown > 0
                  ? `Resend code in ${formatCooldown(resendCooldown)}`
                  : 'Resend the code'}
              </button>
              {isPhone ? (
                <button
                  type="button"
                  onClick={() => setShowEmailFallback((current) => !current)}
                  aria-expanded={showEmailFallback}
                >
                  {showEmailFallback ? 'Cancel email delivery' : "Didn't get it? Send it by email"}
                </button>
              ) : null}
            </div>

            {isPhone && showEmailFallback ? (
              <div className="chow-auth__fallback">
                <Input
                  type="email"
                  value={emailFallbackAddress}
                  onChange={(event) => setEmailFallbackAddress(event.target.value.trim())}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  className="h-12 rounded-[14px] text-[15px]"
                />
                <button
                  type="button"
                  className="chow-auth__secondary"
                  disabled={isPhoneEmailFallbackLoading || !emailFallbackAddress.trim()}
                  onClick={() => void handlePhoneOtpViaEmail()}
                >
                  {isPhoneEmailFallbackLoading ? 'Sending…' : 'Send the code by email'}
                </button>
              </div>
            ) : null}
          </div>

          <div className="chow-auth__foot">
            <button
              type="button"
              className="chow-auth__cta"
              disabled={!codeReady || verifying}
              onClick={() => {
                if (isPhone) void handleVerifyPhoneOtp();
                else void handleVerifyOtp();
              }}
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        overlayStyle={overlayStyle}
        style={authContentStyle}
        onOpenAutoFocus={() => {
          const document = globalThis.document;
          const activeElement = document.activeElement;
          const HTMLElementConstructor = document.defaultView?.HTMLElement;
          openerRef.current =
            HTMLElementConstructor && activeElement instanceof HTMLElementConstructor && activeElement !== document.body
              ? activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const opener = openerRef.current;
          openerRef.current = null;
          if (opener?.isConnected) opener.focus();
        }}
        className={cn(
          'max-w-sm sm:max-w-sm md:max-w-sm lg:max-w-sm rounded-2xl p-6 max-h-[86svh] overflow-y-auto overscroll-contain',
          chow && 'chow-auth-modal chow-root',
        )}
      >
        {/* Header */}
        <div className="text-center mb-5">
          {view !== 'choose' && view !== 'name-prompt' && (
            <button
              onClick={() => { setError(null); setView('choose'); }}
              aria-label="Back"
              className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-md hover:bg-muted transition-colors"
            >
              <ArrowLeft className="size-4" />
            </button>
          )}
          <DialogTitle className="text-lg font-semibold leading-none">{titles[view]}</DialogTitle>
          {view === 'choose' && (
            <p className="text-sm text-muted-foreground mt-1">
              Sign in or create an account to view orders and favorites
            </p>
          )}
          {view === 'name-prompt' && (
            <p className="text-sm text-muted-foreground mt-1">
              This helps us personalize your experience
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2.5 text-xs text-destructive text-center">
            {error}
          </div>
        )}

        {successMessage && view !== 'success' && (
          <div className="mb-4 rounded-lg bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 px-3 py-2.5 text-xs text-[var(--color-success)] text-center">
            {successMessage}
          </div>
        )}

        {/* Choose view: phone first, email as the quiet alternative. Google,
            Apple and passkey buttons are deliberately hidden here (the server
            providers stay registered so they can be re-enabled without a config
            change); passkey remains reachable from account settings. */}
        {view === 'choose' && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Phone number</label>
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                countryCode={country}
                onCountryCodeChange={setCountry}
              />
            </div>
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={handleRequestPhoneOtp}
              disabled={isPhoneOtpLoading || !phoneNumber.trim()}
            >
              {isPhoneOtpLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Smartphone className="size-4 mr-2" />
              )}
              Continue with phone
            </Button>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-2xs text-muted-foreground">
                or
              </span>
            </div>

            <InputField
              label="Email address"
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              type="email"
            />
            <Button
              variant="outline"
              className="w-full h-11 text-sm font-medium"
              onClick={handleRequestOtp}
              disabled={isEmailLoading || !email.trim()}
            >
              {isEmailLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Mail className="size-4 mr-2" />
              )}
              Continue with email
            </Button>
          </div>
        )}

        {/* OTP verify */}
        {view === 'otp-verify' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground text-center">
              We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
            </p>
            <InputField
              label="Verification code"
              value={otp}
              onChange={(v) => setOtp(v.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              type="text"
            />
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={handleVerifyOtp}
              disabled={isOtpLoading || otp.trim().length !== 6}
            >
              {isOtpLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="size-4 mr-2" />
              )}
              Verify
            </Button>
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center disabled:opacity-60 disabled:hover:text-muted-foreground"
              onClick={handleRequestOtp}
              disabled={isEmailLoading || resendCooldown > 0}
            >
              {isEmailLoading
                ? 'Sending...'
                : resendCooldown > 0
                  ? `Resend code in ${formatCooldown(resendCooldown)}`
                  : 'Resend code'}
            </button>
          </div>
        )}

        {/* Phone OTP verify */}
        {view === 'phone-verify' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground text-center">
              {phoneOtpDeliveryUnconfirmed
                ? 'We could not confirm delivery. If the text arrives, enter its code below; otherwise resend after the timer.'
                : <>We sent a 6-digit code by text to <span className="font-medium text-foreground">{phoneNumber}</span></>}
            </p>
            <InputField
              label="Verification code"
              value={otp}
              onChange={(v) => setOtp(v.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              type="text"
            />
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={handleVerifyPhoneOtp}
              disabled={isPhoneVerifyLoading || otp.trim().length !== 6}
            >
              {isPhoneVerifyLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="size-4 mr-2" />
              )}
              Verify
            </Button>
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center disabled:opacity-60 disabled:hover:text-muted-foreground"
              onClick={handleRequestPhoneOtp}
              disabled={isPhoneOtpLoading || resendCooldown > 0}
            >
              {isPhoneOtpLoading
                ? 'Sending...'
                : resendCooldown > 0
                  ? `Resend code in ${formatCooldown(resendCooldown)}`
                  : 'Resend code'}
            </button>
            <div className="space-y-2">
              <button
                type="button"
                className="text-xs text-primary hover:underline transition-colors w-full text-center"
                onClick={() => setShowEmailFallback((current) => !current)}
                aria-expanded={showEmailFallback}
              >
                {showEmailFallback
                  ? 'Cancel email delivery'
                  : "Didn't get the code? Send it to my email instead"}
              </button>
              {showEmailFallback && (
                <div className="space-y-2">
                  <InputField
                    label="Email address"
                    value={emailFallbackAddress}
                    onChange={(v) => setEmailFallbackAddress(v.trim())}
                    placeholder="you@example.com"
                    type="email"
                  />
                  <Button
                    className="w-full h-10 text-sm font-medium"
                    onClick={handlePhoneOtpViaEmail}
                    disabled={isPhoneEmailFallbackLoading || !emailFallbackAddress.trim()}
                  >
                    {isPhoneEmailFallbackLoading ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="size-4 mr-2" />
                    )}
                    Send code to email
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Passkey auth */}
        {view === 'passkey-auth' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground text-center">
              Use your device&apos;s biometric or security key to sign in
            </p>
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={() => handlePasskeyAuth('authenticate')}
              disabled={isPasskeyLoading}
            >
              {isPasskeyLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Fingerprint className="size-4 mr-2" />
              )}
              Sign in with passkey
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              No passkey yet? Set one up from your Chow account settings after signing in.
            </p>
          </div>
        )}

        {/* Passkey register -- only reachable from a signed-in surface (e.g.
            dashboard settings). Registration is session-bound on the server,
            so no name or phone is collected here. */}
        {view === 'passkey-register' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground text-center">
              Sign in with your device&apos;s biometric or security key to add a passkey to this account.
            </p>
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={() => handlePasskeyAuth('register')}
              disabled={isPasskeyLoading}
            >
              {isPasskeyLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Fingerprint className="size-4 mr-2" />
              )}
              Create passkey
            </Button>
          </div>
        )}

        {/* Name + username prompt */}
        {view === 'name-prompt' && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <User className="h-6 w-6 text-primary" />
              </div>
            </div>
            <InputField
              label="Your name"
              value={fullName}
              onChange={(v) => {
                setFullName(v);
                if (!username.trim()) setUsername(suggestUsername(v, email));
              }}
              placeholder="e.g. Kwame Mensah"
            />
            <div>
              <InputField
                label="Username"
                value={username}
                onChange={(v) => setUsername(v.toLowerCase())}
                placeholder="your-handle"
              />
              <p className="mt-1 text-2xs text-muted-foreground">
                Your handle , the name we use to recognise you across businesses. Businesses only ever see your name and username, never your contact details.
              </p>
            </div>
            {isNewSignup && (
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-border p-3.5 text-sm leading-6">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={() => setMarketingOptIn((value) => !value)}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  Send me product updates and occasional offers from Menuraq. You can change this anytime in settings.
                </span>
              </label>
            )}
            <Button
              className="w-full h-11 text-sm font-medium"
              onClick={() => void handleSetProfile()}
              disabled={isNameLoading || !fullName.trim()}
            >
              {isNameLoading ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="size-4 mr-2" />
              )}
              Continue
            </Button>
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
              onClick={() => {
                // The account already exists from the verified code; the name
                // is optional. Business owners sign in through the dashboard,
                // so a customer surface never asks them to pick an account type.
                setView('success');
                setTimeout(() => handleOpenChange(false), 900);
              }}
            >
              Skip for now
            </button>
          </div>
        )}

        {/* Success */}
        {view === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 className="size-10 text-[var(--color-success)]" />
            <p className="text-sm text-muted-foreground">Signed in successfully</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
